/* tslint:disable */
/* eslint-disable */
/**
 * UpContent Platform API
 * # Introduction  The UpContent Public API provides access to UpContent's powerful content curation platform, allowing developers to build powerful content discovery and curation features directly into their applications.  # Authentication  The UpContent Public API uses OAuth2 authorization to grant applications access to UpContent user resources. A client application may request access to a single UpContent account or allow users to authorize access by that client to their UpContent resources.  ## User Authorization  UpContent uses a compliant OAuth2 flow to allow users to authorize access to their resources from third-party applications.  This implies that the access token must be requested from an authenticated client that can maintain the privacy of its credentials. In practice, this means that the access token must be requested from a server, rather than a JavaScript or mobile application.   ### Choosing a Grant Type The `authorization_code` is for applications intended to be use by multiple accounts where an user will grant access to their UpContent account to your application.  The `client_credentials` grant is designed for easy access to your own account without need for a full OAuth2 user authorization grant flow.  ### Choosing a Valid Scope  The UpContent API provides authorization scopes for read and write access based on the type of authorization grant requested. A valid scope must be chosen from the following:  Scope          | Grants                                     | Description ---------------|--------------------------------------------|------------ `client.read`  | `client_credentials`, `authorization_code` | Provides access to most public GET methods outlined in the documentation. `client.write` | `client_credentials`, `authorization_code` | Provides access to most public PUT, POST, and DElETE methods outlined in the documentation. `client.all`   | `client_credentials`, `authorization_code` | Alias for `client.read client.write`. `{module}.read`  | `authorization_code`                       | Provides access to a specific module's public GET methods outlined in the documentation. `{module}.write` | `authorization_code`                       | Provides access to a specific module's public PUT, POST, and DElETE methods outlined in the documentation. `{module}.all`   | `authorization_code`                       | Alias for `{module}.read {module}.write`.  Where `{module}` is one of the following: - team - folders - assignments - subscription - user - collections - collection_content - accounts - topics - topic_content - galleries - templates - quality  Currently module scopes are by special request only. By default when your client credentials are created they are given permission to use client.all.   ### Request Authorization  Begin by redirecting the user to the UpContent OAuth2 authorization page via the following request, with the querystring specified:  ``` GET https://accounts.upcontent.com/authorize  client_id={client_id}& response_type=code& grant_type=authorization_code& state={state}& redirect_uri={redirect_url}& scope={scope} ```  Parameter      | Description ---------------|--------------------------------------------------------------------- `client_id`    | The client identifier assigned to your client by UpContent. `grant_type`   | The OAuth2 grant type that is being used (`authorization_code`). `response_type`| The OAuth2 response type that is being used (`code`) `state`        | A value generated by the client that will be returned to the client to ensure that the authorization code that is returned is associated with an access token request. `redirect_uri` | One of the URLs previously associated with your client during registration with UpContent. The authorization code will be sent back to this URL. `scope`        | A list of space-delimited permissions for which the client is requesting authorization.  An allowed value for the `scope` parameter must be chosen from the table in the section *Choosing a Valid Scope*.  Upon successful authorization, an authorization code will be returned to the given redirect URL with the `state` parameter appended to the querystring, in addition to a `code` parameter which contains the authorization code.  ### Request an Access Token  Once a client has received an authorization code, it may then request an access token and a refresh token. The access token's purpose is to authenticate the client and user to the UpContent API. The purpose of the refresh token is to retrieve a new access token without the need to request a new authorization code.  The following endpoint may be used to request an access token, with parameters submitted as form data:  ``` POST https://accounts.upcontent.com/token  Content-Type: application/x-www-form-urlencoded  grant_type=authorization_code& code={code}& redirect_url={redirect_url}& client_id={client_id}& client_secret={client_secret} ```  Field           | Description ----------------|------------ `grant_type`    | The OAuth2 grant type used in the previous step (`authorization_code`) `code`          | The authorization code obtained in the previous step `redirect_uri`  | The redirect URL to which the code was sent `client_id`     | The client identifier assigned to your client by UpContent `client_secret` | The client secret assigned to your client by UpContent  If an access token is granted, the endpoint will return a **200 response** and a **JSON encoded object** described in the *Access Token* section.   ## Client-only Authorization  The UpContent API also provides a `client_credentials` grant for clients which only need access to a private UpContent account. This grant authorizes a client and generates an access token simultaneously on provision of the credentials generated when the client is registered with UpContent.  The following endpoint may be used to request an access token with this grant type, with parameters submitted as form data:  ``` POST https://accounts.upcontent.com/token  Content-Type: application/x-www-form-urlencoded  grant_type=client_credentials& client_id={client_id}& client_secret={client_secret}& scope={scope} ```  The fields described in the previous section have the same meaning here. The scope parameter here is optional, and if omitted will default to the value `client.all` (see *Choosing a Valid Scope*). If the request is successful, the endpoint will return a **200 response** and a **JSON encoded object** described in the *Access Token* section.  Use of the `client_credentials` grant type can only provide access tokens to the UpContent account of the client's owner.   ## Access Token  On a successful access token request, the UpContent API will return a **200 Response** and a **JSON-encoded object** with the following properties:  Property        | Type    | Description ----------------|---------|------------ `access_token`  | string  | The token used to access the UpContent API. `token_type`    | string  | The type of token. This will be 'bearer'. `expires_in`    | integer | The number of seconds until the token is expected to expire. `refresh_token` | string  | The token used to request a new access token.  The client will be responsible for securing the `access_token` and `refresh_token`, and for updating expired access tokens (see *Refreshing Access*). Note that from the date of issuance, the `access_token` will be valid for `expires_in` seconds and the `refresh_token` will be valid for 30 days.   ## Refreshing Access  Access tokens are generated with an expiration date, after which point they will no longer grant access to the UpContent API. When an access token expires, it can be refreshed using the `refresh_token` provided through the OAuth2 `refresh_token` grant.  This is accomplished making the following request, with parameters submitted as form data:  ``` POST https://accounts.upcontent.com/token  Content-Type: application/x-www-form-urlencoded  grant_type=refresh_token& refresh_token={refresh_token}& scope={scope} ```  The fields described in the previous section have the same meaning here; the field `refresh_token` should be set to the refresh token provided at the time the expired access token was provided. The scope parameter here is optional; if provided, it must be a equal to or a subset of the scope of the expired access token, and if omitted will default to the full scope of the expired token (see *Choosing a Valid Scope*). If the request is successful, the endpoint will return a new access and refresh token in the format defined above.  If both the access and refresh tokens for a user expire, the client must reobtain authorization from that user following the procedure described in *User Authorization*.   ## Accessing the UpContent API  Upon grant of an access token, it can be used until expiration to request data from or send data to the UpContent API endpoints with scopes for which the client is authorized. This is done by including the header ``` Authorization: Bearer {token} ``` in requests to the API, where {token} represents the access token. 
 *
 * OpenAPI spec version: 2.0.0
 * 
 *
 * NOTE: This class is auto generated by the swagger code generator program.
 * https://github.com/swagger-api/swagger-codegen.git
 * Do not edit the class manually.
 */
import globalAxios, { AxiosResponse, AxiosInstance, AxiosRequestConfig } from 'axios';
import { Configuration } from '../configuration';
// Some imports not used depending on template conditions
// @ts-ignore
import { BASE_PATH, COLLECTION_FORMATS, RequestArgs, BaseAPI, RequiredError } from '../base';
import { InlineResponse20012 } from '../models';
import { Suggestion } from '../models';
import { SuggestionIdTypeBody } from '../models';
import { SuggestionURL } from '../models';
import { SuggestiondataBody } from '../models';
import { SuggestionsBody } from '../models';
import { WixSuggestion } from '../models';
/**
 * SuggestionsApi - axios parameter creator
 * @export
 */
export const SuggestionsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @summary Remove a suggestion from the current user's favorites
         * @param {string} suggestion_id UUID for the suggestion
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        favoriteSuggestionsSuggestionIdDelete: async (suggestion_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'suggestion_id' is not null or undefined
            if (suggestion_id === null || suggestion_id === undefined) {
                throw new RequiredError('suggestion_id','Required parameter suggestion_id was null or undefined when calling favoriteSuggestionsSuggestionIdDelete.');
            }
            const localVarPath = `/favorite-suggestions/{suggestion_id}`
                .replace(`{${"suggestion_id"}}`, encodeURIComponent(String(suggestion_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Add a suggestion to the current user's favorites
         * @param {string} suggestion_id UUID for the suggestion
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        favoriteSuggestionsSuggestionIdPut: async (suggestion_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'suggestion_id' is not null or undefined
            if (suggestion_id === null || suggestion_id === undefined) {
                throw new RequiredError('suggestion_id','Required parameter suggestion_id was null or undefined when calling favoriteSuggestionsSuggestionIdPut.');
            }
            const localVarPath = `/favorite-suggestions/{suggestion_id}`
                .replace(`{${"suggestion_id"}}`, encodeURIComponent(String(suggestion_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get aggregate data about a set of suggestions
         * @param {SuggestiondataBody} body Data for querying suggestions and processing the results
         * @param {Array<string>} topic UUIDs for topics to fetch suggestions from
         * @param {Array<string>} collection UUIDs for collections to fetch suggestions from
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        suggestionDataPost: async (body: SuggestiondataBody, topic: Array<string>, collection: Array<string>, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'body' is not null or undefined
            if (body === null || body === undefined) {
                throw new RequiredError('body','Required parameter body was null or undefined when calling suggestionDataPost.');
            }
            // verify required parameter 'topic' is not null or undefined
            if (topic === null || topic === undefined) {
                throw new RequiredError('topic','Required parameter topic was null or undefined when calling suggestionDataPost.');
            }
            // verify required parameter 'collection' is not null or undefined
            if (collection === null || collection === undefined) {
                throw new RequiredError('collection','Required parameter collection was null or undefined when calling suggestionDataPost.');
            }
            const localVarPath = `/suggestion-data`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (topic) {
                localVarQueryParameter['topic'] = topic.join(COLLECTION_FORMATS.csv);
            }

            if (collection) {
                localVarQueryParameter['collection'] = collection.join(COLLECTION_FORMATS.csv);
            }

            localVarHeaderParameter['Content-Type'] = '*/*';

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            const needsSerialization = (typeof body !== "string") || localVarRequestOptions.headers['Content-Type'] === 'application/json';
            localVarRequestOptions.data =  needsSerialization ? JSON.stringify(body !== undefined ? body : {}) : (body || "");

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get a list of suggestions
         * @param {SuggestionsBody} body Data for querying suggestions
         * @param {Array<string>} topic UUIDs for topics to fetch suggestions from
         * @param {Array<string>} collection UUIDs for collections to fetch suggestions from
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        suggestionsPost: async (body: SuggestionsBody, topic: Array<string>, collection: Array<string>, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'body' is not null or undefined
            if (body === null || body === undefined) {
                throw new RequiredError('body','Required parameter body was null or undefined when calling suggestionsPost.');
            }
            // verify required parameter 'topic' is not null or undefined
            if (topic === null || topic === undefined) {
                throw new RequiredError('topic','Required parameter topic was null or undefined when calling suggestionsPost.');
            }
            // verify required parameter 'collection' is not null or undefined
            if (collection === null || collection === undefined) {
                throw new RequiredError('collection','Required parameter collection was null or undefined when calling suggestionsPost.');
            }
            const localVarPath = `/suggestions`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (topic) {
                localVarQueryParameter['topic'] = topic.join(COLLECTION_FORMATS.csv);
            }

            if (collection) {
                localVarQueryParameter['collection'] = collection.join(COLLECTION_FORMATS.csv);
            }

            localVarHeaderParameter['Content-Type'] = '*/*';

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            const needsSerialization = (typeof body !== "string") || localVarRequestOptions.headers['Content-Type'] === 'application/json';
            localVarRequestOptions.data =  needsSerialization ? JSON.stringify(body !== undefined ? body : {}) : (body || "");

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get the contexts in which a suggestion's article appears
         * @param {string} suggestion_id UUID for the suggestion
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        suggestionsSuggestionIdContextsGet: async (suggestion_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'suggestion_id' is not null or undefined
            if (suggestion_id === null || suggestion_id === undefined) {
                throw new RequiredError('suggestion_id','Required parameter suggestion_id was null or undefined when calling suggestionsSuggestionIdContextsGet.');
            }
            const localVarPath = `/suggestions/{suggestion_id}/contexts`
                .replace(`{${"suggestion_id"}}`, encodeURIComponent(String(suggestion_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Delete a suggestion
         * @param {string} suggestion_id UUID for the suggestion
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        suggestionsSuggestionIdDelete: async (suggestion_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'suggestion_id' is not null or undefined
            if (suggestion_id === null || suggestion_id === undefined) {
                throw new RequiredError('suggestion_id','Required parameter suggestion_id was null or undefined when calling suggestionsSuggestionIdDelete.');
            }
            const localVarPath = `/suggestions/{suggestion_id}`
                .replace(`{${"suggestion_id"}}`, encodeURIComponent(String(suggestion_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get the full text of a suggestion
         * @param {string} suggestion_id UUID for the suggestion
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        suggestionsSuggestionIdFulltextGet: async (suggestion_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'suggestion_id' is not null or undefined
            if (suggestion_id === null || suggestion_id === undefined) {
                throw new RequiredError('suggestion_id','Required parameter suggestion_id was null or undefined when calling suggestionsSuggestionIdFulltextGet.');
            }
            const localVarPath = `/suggestions/{suggestion_id}/fulltext`
                .replace(`{${"suggestion_id"}}`, encodeURIComponent(String(suggestion_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get a suggestion
         * @param {string} suggestion_id UUID for the suggestion
         * @param {Array<string>} [include] List of fields to include in response
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        suggestionsSuggestionIdGet: async (suggestion_id: string, include?: Array<string>, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'suggestion_id' is not null or undefined
            if (suggestion_id === null || suggestion_id === undefined) {
                throw new RequiredError('suggestion_id','Required parameter suggestion_id was null or undefined when calling suggestionsSuggestionIdGet.');
            }
            const localVarPath = `/suggestions/{suggestion_id}`
                .replace(`{${"suggestion_id"}}`, encodeURIComponent(String(suggestion_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (include) {
                localVarQueryParameter['include'] = include.join(COLLECTION_FORMATS.csv);
            }

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Update the suggestion type
         * @param {SuggestionIdTypeBody} body Data for assigning a classification to the suggestion
         * @param {string} suggestion_id UUID for the suggestion
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        suggestionsSuggestionIdTypePut: async (body: SuggestionIdTypeBody, suggestion_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'body' is not null or undefined
            if (body === null || body === undefined) {
                throw new RequiredError('body','Required parameter body was null or undefined when calling suggestionsSuggestionIdTypePut.');
            }
            // verify required parameter 'suggestion_id' is not null or undefined
            if (suggestion_id === null || suggestion_id === undefined) {
                throw new RequiredError('suggestion_id','Required parameter suggestion_id was null or undefined when calling suggestionsSuggestionIdTypePut.');
            }
            const localVarPath = `/suggestions/{suggestion_id}/type`
                .replace(`{${"suggestion_id"}}`, encodeURIComponent(String(suggestion_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            localVarHeaderParameter['Content-Type'] = '*/*';

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            const needsSerialization = (typeof body !== "string") || localVarRequestOptions.headers['Content-Type'] === 'application/json';
            localVarRequestOptions.data =  needsSerialization ? JSON.stringify(body !== undefined ? body : {}) : (body || "");

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Unhide a suggestion
         * @param {string} instance_id UUID for the Wix instance
         * @param {string} component_id Unique identifier for the Wix component
         * @param {string} suggestion_id UUID for the suggestion
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        wixInstanceIdComponentIdHiddenSuggestionsSuggestionIdDelete: async (instance_id: string, component_id: string, suggestion_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'instance_id' is not null or undefined
            if (instance_id === null || instance_id === undefined) {
                throw new RequiredError('instance_id','Required parameter instance_id was null or undefined when calling wixInstanceIdComponentIdHiddenSuggestionsSuggestionIdDelete.');
            }
            // verify required parameter 'component_id' is not null or undefined
            if (component_id === null || component_id === undefined) {
                throw new RequiredError('component_id','Required parameter component_id was null or undefined when calling wixInstanceIdComponentIdHiddenSuggestionsSuggestionIdDelete.');
            }
            // verify required parameter 'suggestion_id' is not null or undefined
            if (suggestion_id === null || suggestion_id === undefined) {
                throw new RequiredError('suggestion_id','Required parameter suggestion_id was null or undefined when calling wixInstanceIdComponentIdHiddenSuggestionsSuggestionIdDelete.');
            }
            const localVarPath = `/wix/{instance_id}/{component_id}/hidden-suggestions/{suggestion_id}`
                .replace(`{${"instance_id"}}`, encodeURIComponent(String(instance_id)))
                .replace(`{${"component_id"}}`, encodeURIComponent(String(component_id)))
                .replace(`{${"suggestion_id"}}`, encodeURIComponent(String(suggestion_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Hide a suggestion
         * @param {string} instance_id UUID for the Wix instance
         * @param {string} component_id Unique identifier for the Wix component
         * @param {string} suggestion_id UUID for the suggestion
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        wixInstanceIdComponentIdHiddenSuggestionsSuggestionIdPut: async (instance_id: string, component_id: string, suggestion_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'instance_id' is not null or undefined
            if (instance_id === null || instance_id === undefined) {
                throw new RequiredError('instance_id','Required parameter instance_id was null or undefined when calling wixInstanceIdComponentIdHiddenSuggestionsSuggestionIdPut.');
            }
            // verify required parameter 'component_id' is not null or undefined
            if (component_id === null || component_id === undefined) {
                throw new RequiredError('component_id','Required parameter component_id was null or undefined when calling wixInstanceIdComponentIdHiddenSuggestionsSuggestionIdPut.');
            }
            // verify required parameter 'suggestion_id' is not null or undefined
            if (suggestion_id === null || suggestion_id === undefined) {
                throw new RequiredError('suggestion_id','Required parameter suggestion_id was null or undefined when calling wixInstanceIdComponentIdHiddenSuggestionsSuggestionIdPut.');
            }
            const localVarPath = `/wix/{instance_id}/{component_id}/hidden-suggestions/{suggestion_id}`
                .replace(`{${"instance_id"}}`, encodeURIComponent(String(instance_id)))
                .replace(`{${"component_id"}}`, encodeURIComponent(String(component_id)))
                .replace(`{${"suggestion_id"}}`, encodeURIComponent(String(suggestion_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Unpin a suggestion
         * @param {string} instance_id UUID for the Wix instance
         * @param {string} component_id Unique identifier for the Wix component
         * @param {string} suggestion_id UUID for the suggestion
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        wixInstanceIdComponentIdPinnedSuggestionsSuggestionIdDelete: async (instance_id: string, component_id: string, suggestion_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'instance_id' is not null or undefined
            if (instance_id === null || instance_id === undefined) {
                throw new RequiredError('instance_id','Required parameter instance_id was null or undefined when calling wixInstanceIdComponentIdPinnedSuggestionsSuggestionIdDelete.');
            }
            // verify required parameter 'component_id' is not null or undefined
            if (component_id === null || component_id === undefined) {
                throw new RequiredError('component_id','Required parameter component_id was null or undefined when calling wixInstanceIdComponentIdPinnedSuggestionsSuggestionIdDelete.');
            }
            // verify required parameter 'suggestion_id' is not null or undefined
            if (suggestion_id === null || suggestion_id === undefined) {
                throw new RequiredError('suggestion_id','Required parameter suggestion_id was null or undefined when calling wixInstanceIdComponentIdPinnedSuggestionsSuggestionIdDelete.');
            }
            const localVarPath = `/wix/{instance_id}/{component_id}/pinned-suggestions/{suggestion_id}`
                .replace(`{${"instance_id"}}`, encodeURIComponent(String(instance_id)))
                .replace(`{${"component_id"}}`, encodeURIComponent(String(component_id)))
                .replace(`{${"suggestion_id"}}`, encodeURIComponent(String(suggestion_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Pin a suggestion
         * @param {string} instance_id UUID for the Wix instance
         * @param {string} component_id Unique identifier for the Wix component
         * @param {string} suggestion_id UUID for the suggestion
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        wixInstanceIdComponentIdPinnedSuggestionsSuggestionIdPut: async (instance_id: string, component_id: string, suggestion_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'instance_id' is not null or undefined
            if (instance_id === null || instance_id === undefined) {
                throw new RequiredError('instance_id','Required parameter instance_id was null or undefined when calling wixInstanceIdComponentIdPinnedSuggestionsSuggestionIdPut.');
            }
            // verify required parameter 'component_id' is not null or undefined
            if (component_id === null || component_id === undefined) {
                throw new RequiredError('component_id','Required parameter component_id was null or undefined when calling wixInstanceIdComponentIdPinnedSuggestionsSuggestionIdPut.');
            }
            // verify required parameter 'suggestion_id' is not null or undefined
            if (suggestion_id === null || suggestion_id === undefined) {
                throw new RequiredError('suggestion_id','Required parameter suggestion_id was null or undefined when calling wixInstanceIdComponentIdPinnedSuggestionsSuggestionIdPut.');
            }
            const localVarPath = `/wix/{instance_id}/{component_id}/pinned-suggestions/{suggestion_id}`
                .replace(`{${"instance_id"}}`, encodeURIComponent(String(instance_id)))
                .replace(`{${"component_id"}}`, encodeURIComponent(String(component_id)))
                .replace(`{${"suggestion_id"}}`, encodeURIComponent(String(suggestion_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get the suggestions for this component
         * @param {string} instance_id UUID for the Wix instance
         * @param {string} component_id Unique identifier for the Wix component
         * @param {string} [sort] Sorting method to use
         * @param {number} [page] Result page to return
         * @param {number} [size] Number of results per page to return
         * @param {boolean} [hidden] If true, show only hidden suggestions
         * @param {boolean} [pinned] If true, show only pinned suggestions
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        wixInstanceIdComponentIdSuggestionsGet: async (instance_id: string, component_id: string, sort?: string, page?: number, size?: number, hidden?: boolean, pinned?: boolean, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'instance_id' is not null or undefined
            if (instance_id === null || instance_id === undefined) {
                throw new RequiredError('instance_id','Required parameter instance_id was null or undefined when calling wixInstanceIdComponentIdSuggestionsGet.');
            }
            // verify required parameter 'component_id' is not null or undefined
            if (component_id === null || component_id === undefined) {
                throw new RequiredError('component_id','Required parameter component_id was null or undefined when calling wixInstanceIdComponentIdSuggestionsGet.');
            }
            const localVarPath = `/wix/{instance_id}/{component_id}/suggestions`
                .replace(`{${"instance_id"}}`, encodeURIComponent(String(instance_id)))
                .replace(`{${"component_id"}}`, encodeURIComponent(String(component_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            if (sort !== undefined) {
                localVarQueryParameter['sort'] = sort;
            }

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (size !== undefined) {
                localVarQueryParameter['size'] = size;
            }

            if (hidden !== undefined) {
                localVarQueryParameter['hidden'] = hidden;
            }

            if (pinned !== undefined) {
                localVarQueryParameter['pinned'] = pinned;
            }

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * SuggestionsApi - functional programming interface
 * @export
 */
export const SuggestionsApiFp = function(configuration?: Configuration) {
    return {
        /**
         * 
         * @summary Remove a suggestion from the current user's favorites
         * @param {string} suggestion_id UUID for the suggestion
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async favoriteSuggestionsSuggestionIdDelete(suggestion_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<void>>> {
            const localVarAxiosArgs = await SuggestionsApiAxiosParamCreator(configuration).favoriteSuggestionsSuggestionIdDelete(suggestion_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Add a suggestion to the current user's favorites
         * @param {string} suggestion_id UUID for the suggestion
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async favoriteSuggestionsSuggestionIdPut(suggestion_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<void>>> {
            const localVarAxiosArgs = await SuggestionsApiAxiosParamCreator(configuration).favoriteSuggestionsSuggestionIdPut(suggestion_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Get aggregate data about a set of suggestions
         * @param {SuggestiondataBody} body Data for querying suggestions and processing the results
         * @param {Array<string>} topic UUIDs for topics to fetch suggestions from
         * @param {Array<string>} collection UUIDs for collections to fetch suggestions from
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async suggestionDataPost(body: SuggestiondataBody, topic: Array<string>, collection: Array<string>, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<any>>> {
            const localVarAxiosArgs = await SuggestionsApiAxiosParamCreator(configuration).suggestionDataPost(body, topic, collection, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Get a list of suggestions
         * @param {SuggestionsBody} body Data for querying suggestions
         * @param {Array<string>} topic UUIDs for topics to fetch suggestions from
         * @param {Array<string>} collection UUIDs for collections to fetch suggestions from
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async suggestionsPost(body: SuggestionsBody, topic: Array<string>, collection: Array<string>, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<Array<SuggestionURL>>>> {
            const localVarAxiosArgs = await SuggestionsApiAxiosParamCreator(configuration).suggestionsPost(body, topic, collection, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Get the contexts in which a suggestion's article appears
         * @param {string} suggestion_id UUID for the suggestion
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async suggestionsSuggestionIdContextsGet(suggestion_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<InlineResponse20012>>> {
            const localVarAxiosArgs = await SuggestionsApiAxiosParamCreator(configuration).suggestionsSuggestionIdContextsGet(suggestion_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Delete a suggestion
         * @param {string} suggestion_id UUID for the suggestion
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async suggestionsSuggestionIdDelete(suggestion_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<void>>> {
            const localVarAxiosArgs = await SuggestionsApiAxiosParamCreator(configuration).suggestionsSuggestionIdDelete(suggestion_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Get the full text of a suggestion
         * @param {string} suggestion_id UUID for the suggestion
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async suggestionsSuggestionIdFulltextGet(suggestion_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<string>>> {
            const localVarAxiosArgs = await SuggestionsApiAxiosParamCreator(configuration).suggestionsSuggestionIdFulltextGet(suggestion_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Get a suggestion
         * @param {string} suggestion_id UUID for the suggestion
         * @param {Array<string>} [include] List of fields to include in response
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async suggestionsSuggestionIdGet(suggestion_id: string, include?: Array<string>, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<Suggestion>>> {
            const localVarAxiosArgs = await SuggestionsApiAxiosParamCreator(configuration).suggestionsSuggestionIdGet(suggestion_id, include, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Update the suggestion type
         * @param {SuggestionIdTypeBody} body Data for assigning a classification to the suggestion
         * @param {string} suggestion_id UUID for the suggestion
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async suggestionsSuggestionIdTypePut(body: SuggestionIdTypeBody, suggestion_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<SuggestionIdTypeBody>>> {
            const localVarAxiosArgs = await SuggestionsApiAxiosParamCreator(configuration).suggestionsSuggestionIdTypePut(body, suggestion_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Unhide a suggestion
         * @param {string} instance_id UUID for the Wix instance
         * @param {string} component_id Unique identifier for the Wix component
         * @param {string} suggestion_id UUID for the suggestion
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async wixInstanceIdComponentIdHiddenSuggestionsSuggestionIdDelete(instance_id: string, component_id: string, suggestion_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<void>>> {
            const localVarAxiosArgs = await SuggestionsApiAxiosParamCreator(configuration).wixInstanceIdComponentIdHiddenSuggestionsSuggestionIdDelete(instance_id, component_id, suggestion_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Hide a suggestion
         * @param {string} instance_id UUID for the Wix instance
         * @param {string} component_id Unique identifier for the Wix component
         * @param {string} suggestion_id UUID for the suggestion
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async wixInstanceIdComponentIdHiddenSuggestionsSuggestionIdPut(instance_id: string, component_id: string, suggestion_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<void>>> {
            const localVarAxiosArgs = await SuggestionsApiAxiosParamCreator(configuration).wixInstanceIdComponentIdHiddenSuggestionsSuggestionIdPut(instance_id, component_id, suggestion_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Unpin a suggestion
         * @param {string} instance_id UUID for the Wix instance
         * @param {string} component_id Unique identifier for the Wix component
         * @param {string} suggestion_id UUID for the suggestion
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async wixInstanceIdComponentIdPinnedSuggestionsSuggestionIdDelete(instance_id: string, component_id: string, suggestion_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<void>>> {
            const localVarAxiosArgs = await SuggestionsApiAxiosParamCreator(configuration).wixInstanceIdComponentIdPinnedSuggestionsSuggestionIdDelete(instance_id, component_id, suggestion_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Pin a suggestion
         * @param {string} instance_id UUID for the Wix instance
         * @param {string} component_id Unique identifier for the Wix component
         * @param {string} suggestion_id UUID for the suggestion
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async wixInstanceIdComponentIdPinnedSuggestionsSuggestionIdPut(instance_id: string, component_id: string, suggestion_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<void>>> {
            const localVarAxiosArgs = await SuggestionsApiAxiosParamCreator(configuration).wixInstanceIdComponentIdPinnedSuggestionsSuggestionIdPut(instance_id, component_id, suggestion_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Get the suggestions for this component
         * @param {string} instance_id UUID for the Wix instance
         * @param {string} component_id Unique identifier for the Wix component
         * @param {string} [sort] Sorting method to use
         * @param {number} [page] Result page to return
         * @param {number} [size] Number of results per page to return
         * @param {boolean} [hidden] If true, show only hidden suggestions
         * @param {boolean} [pinned] If true, show only pinned suggestions
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async wixInstanceIdComponentIdSuggestionsGet(instance_id: string, component_id: string, sort?: string, page?: number, size?: number, hidden?: boolean, pinned?: boolean, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<Array<WixSuggestion>>>> {
            const localVarAxiosArgs = await SuggestionsApiAxiosParamCreator(configuration).wixInstanceIdComponentIdSuggestionsGet(instance_id, component_id, sort, page, size, hidden, pinned, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
    }
};

/**
 * SuggestionsApi - factory interface
 * @export
 */
export const SuggestionsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    return {
        /**
         * 
         * @summary Remove a suggestion from the current user's favorites
         * @param {string} suggestion_id UUID for the suggestion
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async favoriteSuggestionsSuggestionIdDelete(suggestion_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<void>> {
            return SuggestionsApiFp(configuration).favoriteSuggestionsSuggestionIdDelete(suggestion_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Add a suggestion to the current user's favorites
         * @param {string} suggestion_id UUID for the suggestion
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async favoriteSuggestionsSuggestionIdPut(suggestion_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<void>> {
            return SuggestionsApiFp(configuration).favoriteSuggestionsSuggestionIdPut(suggestion_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get aggregate data about a set of suggestions
         * @param {SuggestiondataBody} body Data for querying suggestions and processing the results
         * @param {Array<string>} topic UUIDs for topics to fetch suggestions from
         * @param {Array<string>} collection UUIDs for collections to fetch suggestions from
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async suggestionDataPost(body: SuggestiondataBody, topic: Array<string>, collection: Array<string>, options?: AxiosRequestConfig): Promise<AxiosResponse<any>> {
            return SuggestionsApiFp(configuration).suggestionDataPost(body, topic, collection, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get a list of suggestions
         * @param {SuggestionsBody} body Data for querying suggestions
         * @param {Array<string>} topic UUIDs for topics to fetch suggestions from
         * @param {Array<string>} collection UUIDs for collections to fetch suggestions from
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async suggestionsPost(body: SuggestionsBody, topic: Array<string>, collection: Array<string>, options?: AxiosRequestConfig): Promise<AxiosResponse<Array<SuggestionURL>>> {
            return SuggestionsApiFp(configuration).suggestionsPost(body, topic, collection, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get the contexts in which a suggestion's article appears
         * @param {string} suggestion_id UUID for the suggestion
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async suggestionsSuggestionIdContextsGet(suggestion_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<InlineResponse20012>> {
            return SuggestionsApiFp(configuration).suggestionsSuggestionIdContextsGet(suggestion_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Delete a suggestion
         * @param {string} suggestion_id UUID for the suggestion
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async suggestionsSuggestionIdDelete(suggestion_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<void>> {
            return SuggestionsApiFp(configuration).suggestionsSuggestionIdDelete(suggestion_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get the full text of a suggestion
         * @param {string} suggestion_id UUID for the suggestion
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async suggestionsSuggestionIdFulltextGet(suggestion_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<string>> {
            return SuggestionsApiFp(configuration).suggestionsSuggestionIdFulltextGet(suggestion_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get a suggestion
         * @param {string} suggestion_id UUID for the suggestion
         * @param {Array<string>} [include] List of fields to include in response
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async suggestionsSuggestionIdGet(suggestion_id: string, include?: Array<string>, options?: AxiosRequestConfig): Promise<AxiosResponse<Suggestion>> {
            return SuggestionsApiFp(configuration).suggestionsSuggestionIdGet(suggestion_id, include, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Update the suggestion type
         * @param {SuggestionIdTypeBody} body Data for assigning a classification to the suggestion
         * @param {string} suggestion_id UUID for the suggestion
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async suggestionsSuggestionIdTypePut(body: SuggestionIdTypeBody, suggestion_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<SuggestionIdTypeBody>> {
            return SuggestionsApiFp(configuration).suggestionsSuggestionIdTypePut(body, suggestion_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Unhide a suggestion
         * @param {string} instance_id UUID for the Wix instance
         * @param {string} component_id Unique identifier for the Wix component
         * @param {string} suggestion_id UUID for the suggestion
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async wixInstanceIdComponentIdHiddenSuggestionsSuggestionIdDelete(instance_id: string, component_id: string, suggestion_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<void>> {
            return SuggestionsApiFp(configuration).wixInstanceIdComponentIdHiddenSuggestionsSuggestionIdDelete(instance_id, component_id, suggestion_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Hide a suggestion
         * @param {string} instance_id UUID for the Wix instance
         * @param {string} component_id Unique identifier for the Wix component
         * @param {string} suggestion_id UUID for the suggestion
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async wixInstanceIdComponentIdHiddenSuggestionsSuggestionIdPut(instance_id: string, component_id: string, suggestion_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<void>> {
            return SuggestionsApiFp(configuration).wixInstanceIdComponentIdHiddenSuggestionsSuggestionIdPut(instance_id, component_id, suggestion_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Unpin a suggestion
         * @param {string} instance_id UUID for the Wix instance
         * @param {string} component_id Unique identifier for the Wix component
         * @param {string} suggestion_id UUID for the suggestion
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async wixInstanceIdComponentIdPinnedSuggestionsSuggestionIdDelete(instance_id: string, component_id: string, suggestion_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<void>> {
            return SuggestionsApiFp(configuration).wixInstanceIdComponentIdPinnedSuggestionsSuggestionIdDelete(instance_id, component_id, suggestion_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Pin a suggestion
         * @param {string} instance_id UUID for the Wix instance
         * @param {string} component_id Unique identifier for the Wix component
         * @param {string} suggestion_id UUID for the suggestion
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async wixInstanceIdComponentIdPinnedSuggestionsSuggestionIdPut(instance_id: string, component_id: string, suggestion_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<void>> {
            return SuggestionsApiFp(configuration).wixInstanceIdComponentIdPinnedSuggestionsSuggestionIdPut(instance_id, component_id, suggestion_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get the suggestions for this component
         * @param {string} instance_id UUID for the Wix instance
         * @param {string} component_id Unique identifier for the Wix component
         * @param {string} [sort] Sorting method to use
         * @param {number} [page] Result page to return
         * @param {number} [size] Number of results per page to return
         * @param {boolean} [hidden] If true, show only hidden suggestions
         * @param {boolean} [pinned] If true, show only pinned suggestions
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async wixInstanceIdComponentIdSuggestionsGet(instance_id: string, component_id: string, sort?: string, page?: number, size?: number, hidden?: boolean, pinned?: boolean, options?: AxiosRequestConfig): Promise<AxiosResponse<Array<WixSuggestion>>> {
            return SuggestionsApiFp(configuration).wixInstanceIdComponentIdSuggestionsGet(instance_id, component_id, sort, page, size, hidden, pinned, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * SuggestionsApi - object-oriented interface
 * @export
 * @class SuggestionsApi
 * @extends {BaseAPI}
 */
export class SuggestionsApi extends BaseAPI {
    /**
     * 
     * @summary Remove a suggestion from the current user's favorites
     * @param {string} suggestion_id UUID for the suggestion
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof SuggestionsApi
     */
    public async favoriteSuggestionsSuggestionIdDelete(suggestion_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<void>> {
        return SuggestionsApiFp(this.configuration).favoriteSuggestionsSuggestionIdDelete(suggestion_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Add a suggestion to the current user's favorites
     * @param {string} suggestion_id UUID for the suggestion
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof SuggestionsApi
     */
    public async favoriteSuggestionsSuggestionIdPut(suggestion_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<void>> {
        return SuggestionsApiFp(this.configuration).favoriteSuggestionsSuggestionIdPut(suggestion_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Get aggregate data about a set of suggestions
     * @param {SuggestiondataBody} body Data for querying suggestions and processing the results
     * @param {Array<string>} topic UUIDs for topics to fetch suggestions from
     * @param {Array<string>} collection UUIDs for collections to fetch suggestions from
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof SuggestionsApi
     */
    public async suggestionDataPost(body: SuggestiondataBody, topic: Array<string>, collection: Array<string>, options?: AxiosRequestConfig) : Promise<AxiosResponse<any>> {
        return SuggestionsApiFp(this.configuration).suggestionDataPost(body, topic, collection, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Get a list of suggestions
     * @param {SuggestionsBody} body Data for querying suggestions
     * @param {Array<string>} topic UUIDs for topics to fetch suggestions from
     * @param {Array<string>} collection UUIDs for collections to fetch suggestions from
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof SuggestionsApi
     */
    public async suggestionsPost(body: SuggestionsBody, topic: Array<string>, collection: Array<string>, options?: AxiosRequestConfig) : Promise<AxiosResponse<Array<SuggestionURL>>> {
        return SuggestionsApiFp(this.configuration).suggestionsPost(body, topic, collection, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Get the contexts in which a suggestion's article appears
     * @param {string} suggestion_id UUID for the suggestion
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof SuggestionsApi
     */
    public async suggestionsSuggestionIdContextsGet(suggestion_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<InlineResponse20012>> {
        return SuggestionsApiFp(this.configuration).suggestionsSuggestionIdContextsGet(suggestion_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Delete a suggestion
     * @param {string} suggestion_id UUID for the suggestion
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof SuggestionsApi
     */
    public async suggestionsSuggestionIdDelete(suggestion_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<void>> {
        return SuggestionsApiFp(this.configuration).suggestionsSuggestionIdDelete(suggestion_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Get the full text of a suggestion
     * @param {string} suggestion_id UUID for the suggestion
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof SuggestionsApi
     */
    public async suggestionsSuggestionIdFulltextGet(suggestion_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<string>> {
        return SuggestionsApiFp(this.configuration).suggestionsSuggestionIdFulltextGet(suggestion_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Get a suggestion
     * @param {string} suggestion_id UUID for the suggestion
     * @param {Array<string>} [include] List of fields to include in response
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof SuggestionsApi
     */
    public async suggestionsSuggestionIdGet(suggestion_id: string, include?: Array<string>, options?: AxiosRequestConfig) : Promise<AxiosResponse<Suggestion>> {
        return SuggestionsApiFp(this.configuration).suggestionsSuggestionIdGet(suggestion_id, include, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Update the suggestion type
     * @param {SuggestionIdTypeBody} body Data for assigning a classification to the suggestion
     * @param {string} suggestion_id UUID for the suggestion
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof SuggestionsApi
     */
    public async suggestionsSuggestionIdTypePut(body: SuggestionIdTypeBody, suggestion_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<SuggestionIdTypeBody>> {
        return SuggestionsApiFp(this.configuration).suggestionsSuggestionIdTypePut(body, suggestion_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Unhide a suggestion
     * @param {string} instance_id UUID for the Wix instance
     * @param {string} component_id Unique identifier for the Wix component
     * @param {string} suggestion_id UUID for the suggestion
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof SuggestionsApi
     */
    public async wixInstanceIdComponentIdHiddenSuggestionsSuggestionIdDelete(instance_id: string, component_id: string, suggestion_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<void>> {
        return SuggestionsApiFp(this.configuration).wixInstanceIdComponentIdHiddenSuggestionsSuggestionIdDelete(instance_id, component_id, suggestion_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Hide a suggestion
     * @param {string} instance_id UUID for the Wix instance
     * @param {string} component_id Unique identifier for the Wix component
     * @param {string} suggestion_id UUID for the suggestion
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof SuggestionsApi
     */
    public async wixInstanceIdComponentIdHiddenSuggestionsSuggestionIdPut(instance_id: string, component_id: string, suggestion_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<void>> {
        return SuggestionsApiFp(this.configuration).wixInstanceIdComponentIdHiddenSuggestionsSuggestionIdPut(instance_id, component_id, suggestion_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Unpin a suggestion
     * @param {string} instance_id UUID for the Wix instance
     * @param {string} component_id Unique identifier for the Wix component
     * @param {string} suggestion_id UUID for the suggestion
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof SuggestionsApi
     */
    public async wixInstanceIdComponentIdPinnedSuggestionsSuggestionIdDelete(instance_id: string, component_id: string, suggestion_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<void>> {
        return SuggestionsApiFp(this.configuration).wixInstanceIdComponentIdPinnedSuggestionsSuggestionIdDelete(instance_id, component_id, suggestion_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Pin a suggestion
     * @param {string} instance_id UUID for the Wix instance
     * @param {string} component_id Unique identifier for the Wix component
     * @param {string} suggestion_id UUID for the suggestion
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof SuggestionsApi
     */
    public async wixInstanceIdComponentIdPinnedSuggestionsSuggestionIdPut(instance_id: string, component_id: string, suggestion_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<void>> {
        return SuggestionsApiFp(this.configuration).wixInstanceIdComponentIdPinnedSuggestionsSuggestionIdPut(instance_id, component_id, suggestion_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Get the suggestions for this component
     * @param {string} instance_id UUID for the Wix instance
     * @param {string} component_id Unique identifier for the Wix component
     * @param {string} [sort] Sorting method to use
     * @param {number} [page] Result page to return
     * @param {number} [size] Number of results per page to return
     * @param {boolean} [hidden] If true, show only hidden suggestions
     * @param {boolean} [pinned] If true, show only pinned suggestions
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof SuggestionsApi
     */
    public async wixInstanceIdComponentIdSuggestionsGet(instance_id: string, component_id: string, sort?: string, page?: number, size?: number, hidden?: boolean, pinned?: boolean, options?: AxiosRequestConfig) : Promise<AxiosResponse<Array<WixSuggestion>>> {
        return SuggestionsApiFp(this.configuration).wixInstanceIdComponentIdSuggestionsGet(instance_id, component_id, sort, page, size, hidden, pinned, options).then((request) => request(this.axios, this.basePath));
    }
}
